import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Truck } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "./smooth";
import { LangSwitcher } from "./LangSwitcher";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const LINK_IDS = ["services", "european", "gallery", "quote", "areas", "contact"];

const LiveDot = () => (
  <span className="relative inline-flex h-2 w-2">
    <span className="ping-soft absolute inset-0 rounded-full bg-emerald-400" />
    <span className="relative h-2 w-2 rounded-full bg-emerald-400" />
  </span>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const navigate = useNavigate();
  const LINKS = LINK_IDS.map((id) => [id, t.nav.links[id]]);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (id === "quote") navigate("/quote");
    else scrollToId(id);
  };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${scrolled ? "border-b border-white/10 bg-ink/70 backdrop-blur-xl" : "border-b border-transparent"}`}
      >
        <div data-testid="top-bar" className="bg-brand text-ink">
          <div className="mx-auto flex h-8 max-w-[1440px] items-center justify-between gap-3 px-5 md:px-10">
            <span className="inline-flex min-w-0 items-center gap-2 truncate font-mono text-[11px] uppercase tracking-[0.14em]"><Truck className="h-3.5 w-3.5 shrink-0" /><b>24/7</b><span className="hidden truncate sm:inline"> · {t.nav.mobileBar}</span></span>
            <LangSwitcher />
          </div>
        </div>
        <div className="mx-auto flex h-[88px] max-w-[1440px] items-center justify-between px-5 md:h-[112px] md:px-10">
          <button data-testid="nav-logo-button" onClick={() => go("top")} className="shrink-0"><Logo /></button>
          <nav className="hidden items-center gap-7 xl:flex">
            {LINKS.map(([id, label]) => (
              <button key={id} data-testid={`nav-link-${id}`} onClick={() => go(id)} className="group relative font-mono text-[12px] uppercase tracking-[0.18em] text-zinc-300 transition-colors hover:text-bone">
                {label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-brand transition-[width] duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="hidden items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-300 xl:inline-flex"><LiveDot /> {t.nav.openNow}</span>
            <a data-testid="nav-call-button" href={PHONE_TEL} className="group inline-flex items-center gap-2 bg-brand px-4 py-2.5 text-ink transition-transform duration-300 hover:-translate-y-0.5">
              <Phone className="h-4 w-4" />
              <span className="font-display hidden whitespace-nowrap text-[12px] font-bold tracking-wider sm:inline">{PHONE_DISPLAY}</span>
              <span className="font-display text-[12px] font-bold tracking-wider sm:hidden">{t.nav.call}</span>
            </a>
            <button data-testid="nav-menu-toggle" onClick={() => setOpen((v) => !v)} className="grid h-10 w-10 place-items-center border border-white/15 text-bone xl:hidden" aria-label="Menu">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </motion.header>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 z-40 flex flex-col justify-end bg-ink px-6 pb-14 pt-32 xl:hidden"
          >
            {LINKS.map(([id, label], i) => (
              <motion.button key={id} data-testid={`mobile-link-${id}`} onClick={() => go(id)} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.05 }} className="font-display border-b border-white/10 py-4 text-left text-3xl font-extrabold uppercase text-bone">
                {label}
              </motion.button>
            ))}
            <span className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-emerald-300"><LiveDot /> {t.nav.menuTag}</span>
          </motion.div>
        )}
      </AnimatePresence>
      <a data-testid="mobile-sticky-call" href={PHONE_TEL} className="fixed inset-x-3 bottom-3 z-40 flex items-center justify-center gap-3 bg-brand py-3.5 text-ink shadow-[0_10px_40px_-10px_rgb(var(--brand)/0.7)] md:hidden">
        <Phone className="h-4 w-4" /><span className="font-display text-sm font-bold tracking-wider">{t.nav.sticky} · {PHONE_DISPLAY}</span>
      </a>
    </>
  );
};
